import * as React from "react";
import {Grid} from "@material-ui/core";
import styled from "styled-components";
import {ApplicationStatusEnum} from "../../models/application-status.enum";
import {AuthMeModel} from "../../models/auth-me.model";
import {RootState} from "../../store/root-reducer";
import {useSelector, useDispatch} from "react-redux";
import {AccountTypeEnum} from "../../models/account-type.enum";
import {ApplicationService} from "../../services/application.service";
import {showSnackSuccess} from "../../store/global/global-slice";
import {ConfirmDialog} from "../confirm-dialog";
import {StyledSmallButton} from "../ui-base/small-button";
import {useTranslation} from "react-i18next";
import {PublisherService} from "../../services/publisher.service";

const BookStatus: React.FC<Props> = ({id, status, updateStatus, bookmarked, vertical, publisherToPublisher = false, useAutType = false, alignItems = 'flex-end'}) => {
    const [openConfirmDialog, setOpenConfirmDialog] = React.useState<boolean>(false);
    const [textConfirmDialog, setTextConfirmDialog] = React.useState<string>('');
    const [confirmDialogFunctionName, setConfirmDialogFunctionName] = React.useState<string>();
    const dispatch = useDispatch();
    const { t } = useTranslation('common');
    const user: AuthMeModel = useSelector(
        (state: RootState) => state.auth.user
    );

    const addToBookmark = async () => {
        let res = null;

        if (!publisherToPublisher) {
            res = await ApplicationService.addToBookmark(id);
        } else {
            res = await PublisherService.bookPropositionAddBookmark(id);
        }

        if (res.status) {
            dispatch(showSnackSuccess(t('manuscriptStatus.statusChanged')));
            updateStatus(res.data.status);
        }
    }

    const publisherDoesntWantToPublish = async () => {
        let res = null;

        if (!publisherToPublisher) {
            res = await ApplicationService.publisherDoesntWantToPublish(id);
        } else {
            res = await PublisherService.bookPropositionPubDoesntWantToPublish(id);
        }

        if (res.status) {
            dispatch(showSnackSuccess(t('manuscriptStatus.statusChanged')));
            updateStatus(res.data.status);
        }
    }

    const publisherWantToPublish = async () => {
        let res = null;

        if (!publisherToPublisher) {
            res = await ApplicationService.publisherWantToPublish(id);
        } else {
            res = await PublisherService.bookPropositionPubWantToPublish(id);
        }

        if (res.status) {
            dispatch(showSnackSuccess(t('manuscriptStatus.statusChanged')));
            updateStatus(res.data.status);
        }
    }

    const authorDoesntWantToPublish = async () => {
        let res = null;

        if (!publisherToPublisher) {
            res = await ApplicationService.authorDoesntWantToPublish(id);
        } else {
            res = await PublisherService.pubFromDoesntWantToPublish(id);
        }

        if (res.status) {
            dispatch(showSnackSuccess(t('manuscriptStatus.statusChanged')));
            updateStatus(res.data.status);
        }
    }

    const authorWantToPublish = async () => {
        let res = null;

        if (!publisherToPublisher) {
            res = await ApplicationService.authorWantToPublish(id);
        } else {
            res = await PublisherService.pubFromWantToPublish(id);
        }

        if (res.status) {
            dispatch(showSnackSuccess(t('manuscriptStatus.statusChanged')));
            updateStatus(res.data.status);
        }
    }

    const success = async () => {
        let res = null;

        if (!publisherToPublisher) {
            res = await ApplicationService.success(id);
        } else {
            res = await PublisherService.success(id);
        }

        if (res.status) {
            dispatch(showSnackSuccess(t('manuscriptStatus.statusChanged')));
            updateStatus(res.data.status);
        }
    }

    const fail = async () => {
        let res = null;

        if (!publisherToPublisher) {
            res = await ApplicationService.fail(id);
        } else {
            res = await PublisherService.fail(id);
        }

        if (res.status) {
            dispatch(showSnackSuccess(t('manuscriptStatus.statusChanged')));
            updateStatus(res.data.status);
        }
    }

    const confirmDialogOnClose = () => {
        switch (confirmDialogFunctionName) {
            case 'publisherWantToPublish':
                publisherWantToPublish();
                break;
            case 'addToBookmark':
                addToBookmark();
                break;
            case 'publisherDoesntWantToPublish':
                publisherDoesntWantToPublish();
                break;
            case 'authorDoesntWantToPublish':
                authorDoesntWantToPublish();
                break;
            case 'authorWantToPublish':
                authorWantToPublish();
                break;
            case 'success':
                success();
                break;
            case 'fail':
                fail();
                break;
        }
    }

    return (
        <Grid container direction={"column"} alignItems={alignItems}>
            <ConfirmDialog
                open={openConfirmDialog}
                onClose={(value) => {
                    setTextConfirmDialog('');
                    setOpenConfirmDialog(false);

                    if (value && confirmDialogFunctionName) {
                        confirmDialogOnClose();
                    }
                }}
                title={t('manuscriptStatus.statusChange')}
                text={textConfirmDialog}
            />
            <StyledMainGrid item container direction={(!vertical) ? "row" : "column"} spacing={1}>
                {user?.account_type === AccountTypeEnum.PUB && !useAutType &&
                    <>
                        {(status === ApplicationStatusEnum.OpenByPub) && !bookmarked &&
                        <>
                            <Grid item>
                                <StyledSmallButton type={"button"} color={"transparent"} onClick={() => {
                                    setConfirmDialogFunctionName('addToBookmark');
                                    setTextConfirmDialog(t('manuscriptStatus.changeStatus'));
                                    setOpenConfirmDialog(true);
                                }}>
                                    {t('manuscriptStatus.saveToBookmark')}
                                </StyledSmallButton>
                            </Grid>

                            <Grid item>
                                <StyledSmallButton type={"button"} onClick={() => {
                                    setConfirmDialogFunctionName('publisherWantToPublish');
                                    setTextConfirmDialog(t('manuscriptStatus.changeStatus'));
                                    setOpenConfirmDialog(true);
                                }}>
                                    {t('manuscriptStatus.wantToPublish')}
                                </StyledSmallButton>
                            </Grid>

                            <Grid item>
                                <StyledSmallButton type={"button"} color={"red"} onClick={() => {
                                    setConfirmDialogFunctionName('publisherDoesntWantToPublish');
                                    setTextConfirmDialog(t('manuscriptStatus.changeStatus'));
                                    setOpenConfirmDialog(true);
                                }}>
                                    {t('manuscriptStatus.wontPublish')}
                                </StyledSmallButton>
                            </Grid>
                        </>
                        }

                        {bookmarked && (status === ApplicationStatusEnum.OpenByPub) &&
                            <>
                                <Grid item>
                                    <StyledSmallButton type={"button"} onClick={() => {
                                        setConfirmDialogFunctionName('publisherWantToPublish');
                                        setTextConfirmDialog(t('manuscriptStatus.changeStatus'));
                                        setOpenConfirmDialog(true);
                                    }}>
                                        {t('manuscriptStatus.wantToPublish')}
                                    </StyledSmallButton>
                                </Grid>

                                <Grid item>
                                    <StyledSmallButton type={"button"} color={"red"} onClick={() => {
                                        setConfirmDialogFunctionName('publisherDoesntWantToPublish');
                                        setTextConfirmDialog(t('manuscriptStatus.changeStatus'));
                                        setOpenConfirmDialog(true);
                                    }}>
                                        {t('manuscriptStatus.wontPublish')}
                                    </StyledSmallButton>
                                </Grid>
                            </>
                        }

                        {status === ApplicationStatusEnum.PubWant && !bookmarked &&
                        <>
                            <Grid item>
                                <StyledSmallButton type={"button"} color={"red"} onClick={() => {
                                    setConfirmDialogFunctionName('publisherDoesntWantToPublish');
                                    setTextConfirmDialog(t('manuscriptStatus.changeStatus'));
                                    setOpenConfirmDialog(true);
                                }}>
                                    {t('manuscriptStatus.wontPublish')}
                                </StyledSmallButton>
                            </Grid>
                        </>
                        }
                    </>
                }

                {(user?.account_type === AccountTypeEnum.AUT || useAutType) &&
                <>
                    {status === ApplicationStatusEnum.PubWant && !bookmarked &&
                        <>
                            <Grid item>
                                <StyledSmallButton type={"button"} onClick={() => {
                                    setConfirmDialogFunctionName('authorWantToPublish');
                                    setTextConfirmDialog(t('manuscriptStatus.changeStatus'));
                                    setOpenConfirmDialog(true);
                                }}>
                                    {t('manuscriptStatus.wantToPublish')}
                                </StyledSmallButton>
                            </Grid>

                            <Grid item>
                                <StyledSmallButton type={"button"} color={"red"} onClick={() => {
                                    setConfirmDialogFunctionName('authorDoesntWantToPublish');
                                    setTextConfirmDialog(t('manuscriptStatus.changeStatus'));
                                    setOpenConfirmDialog(true);
                                }}>
                                    {t('manuscriptStatus.wontPublish')}
                                </StyledSmallButton>
                            </Grid>
                        </>
                    }

                    {status === ApplicationStatusEnum.AutWant && !bookmarked &&
                    <>
                        <Grid item>
                            <StyledSmallButton type={"button"} onClick={() => {
                                setConfirmDialogFunctionName('success');
                                setTextConfirmDialog(t('manuscriptStatus.changeStatus'));
                                setOpenConfirmDialog(true);
                            }}>
                                {t('manuscriptStatus.bookPublished')}
                            </StyledSmallButton>
                        </Grid>

                        <Grid item>
                            <StyledSmallButton type={"button"} color={"red"} onClick={() => {
                                setConfirmDialogFunctionName('fail');
                                setTextConfirmDialog(t('manuscriptStatus.changeStatus'));
                                setOpenConfirmDialog(true);
                            }}>
                                {t('manuscriptStatus.bookNotPublished')}
                            </StyledSmallButton>
                        </Grid>
                    </>
                    }
                </>
                }
            </StyledMainGrid>
        </Grid>

    )
}

interface Props {
    status: ApplicationStatusEnum,
    id: string,
    updateStatus: (status: ApplicationStatusEnum) => void,
    bookmarked: boolean,
    vertical?: boolean,
    publisherToPublisher?: boolean,
    useAutType?: boolean,
    alignItems?: 'flex-end' | 'center'
}

const StyledMainGrid = styled(Grid)`
  width: auto;
`;

export default BookStatus;
